import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import type { Customer, Marketer, Order, Page, Plan, Product } from "../lib/types";
import {
  Button,
  Card,
  EmptyState,
  ErrorNote,
  Field,
  Input,
  OrderStatusBadge,
  PageHeader,
  Select,
  Table,
  TableWrap,
  Td,
  Th,
  centsFromInput,
  formatDate,
  formatMoney,
} from "../components/ui";

const STATUSES = ["draft", "awaiting_payment", "paid", "cancelled", "refunded"] as const;

const HEADINGS: Record<string, { title: string; blurb: string }> = {
  admin: { title: "Orders", blurb: "Every sale, and what it turned into." },
  marketer: { title: "Your sales", blurb: "Orders attributed to you, and what each one earned." },
  customer: { title: "Your orders", blurb: "What you have bought, and the licence it issued." },
};

export function Orders() {
  const { user } = useAuth();
  const role = user?.role ?? "customer";
  const isAdmin = role === "admin";
  const heading = HEADINGS[role] ?? HEADINGS["customer"]!;

  const [status, setStatus] = useState("");
  const [cursor, setCursor] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const params = new URLSearchParams();
  if (status.length > 0) params.set("status", status);
  if (cursor !== null) params.set("cursor", cursor);

  const orders = useQuery({
    queryKey: ["orders", params.toString()],
    queryFn: () => api<Page<Order>>(`/api/v1/admin/orders?${params.toString()}`),
  });

  return (
    <div className="space-y-6">
      <PageHeader
        title={heading.title}
        lede={heading.blurb}
        action={
          isAdmin ? (
            <Button variant="primary" onClick={() => setCreating(true)}>
              New order
            </Button>
          ) : undefined
        }
      />

      <div className="w-48">
        <Field label="Status">
          <Select
            value={status}
            onChange={(event) => {
              setStatus(event.target.value);
              setCursor(null);
            }}
          >
            <option value="">All</option>
            {STATUSES.map((value) => (
              <option key={value} value={value}>
                {value.replace("_", " ")}
              </option>
            ))}
          </Select>
        </Field>
      </div>

      <Card>
        {orders.isPending ? (
          <EmptyState>Loading…</EmptyState>
        ) : orders.isError ? (
          <div className="p-4">
            <ErrorNote>Could not load orders. Reload to try again.</ErrorNote>
          </div>
        ) : orders.data.items.length === 0 ? (
          <EmptyState>
            {status.length > 0 ? "No orders with that status." : "No orders yet."}
          </EmptyState>
        ) : (
          <TableWrap>
            <Table>
              <thead className="border-b border-hairline">
                <tr>
                  <Th>Order</Th>
                  {role !== "customer" && <Th>Customer</Th>}
                  <Th>Product</Th>
                  <Th>One time</Th>
                  <Th>Maintenance</Th>
                  <Th>Status</Th>
                  {role === "marketer" && <Th>Commission</Th>}
                  {role !== "marketer" && <Th>Licence</Th>}
                  <Th>Created</Th>
                </tr>
              </thead>
              <tbody>
                {orders.data.items.map((order) => (
                  <tr key={order.id} className="border-b border-hairline last:border-0">
                    <Td label="Order">
                      <Link
                        to={`/orders/${order.id}`}
                        className="tabular text-accent underline-offset-4 hover:underline"
                      >
                        {order.number}
                      </Link>
                    </Td>
                    {role !== "customer" && (
                      <Td label="Customer">
                        {/* One flex item once the row is a card, so the name and
                            the address stay stacked rather than sitting apart. */}
                        <span className="block">
                          <span className="block">{order.customer.name}</span>
                          <span className="block text-xs text-dim">{order.customer.email}</span>
                        </span>
                      </Td>
                    )}
                    <Td label="Product" className="text-muted">
                      <span className="block">
                        <span className="block">{order.product.name}</span>
                        <span className="block text-xs text-dim">{order.plan.name}</span>
                      </span>
                    </Td>
                    <Td label="One time" className="tabular">
                      {formatMoney(order.oneTimeCents, order.currency)}
                    </Td>
                    <Td label="Maintenance" className="tabular text-muted">
                      <span className="block">
                        {formatMoney(order.plan.priceCents, order.plan.currency ?? order.currency)}
                        <span className="text-dim"> / {order.plan.interval}</span>
                      </span>
                    </Td>
                    <Td label="Status">
                      <OrderStatusBadge status={order.status} />
                    </Td>
                    {role === "marketer" && (
                      <Td label="Commission" className="tabular">
                        {formatMoney(order.commissionCents, order.currency)}
                      </Td>
                    )}
                    {role !== "marketer" && (
                      <Td label="Licence" className="tabular text-muted">
                        {order.license === null ? (
                          <span className="text-dim">—</span>
                        ) : (
                          <Link
                            to={`/licenses/${order.license.id}`}
                            className="text-accent underline-offset-4 hover:underline"
                          >
                            ••••{order.license.keyLast4}
                          </Link>
                        )}
                      </Td>
                    )}
                    <Td label="Created" className="text-muted">
                      {formatDate(order.createdAt)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrap>
        )}
      </Card>

      {(cursor !== null || orders.data?.nextCursor != null) && (
        <div className="flex justify-between">
          <Button onClick={() => setCursor(null)} disabled={cursor === null}>
            First page
          </Button>
          <Button
            onClick={() => setCursor(orders.data?.nextCursor ?? null)}
            disabled={orders.data?.nextCursor == null}
          >
            Next page
          </Button>
        </div>
      )}

      {creating && <NewOrder onClose={() => setCreating(false)} />}
    </div>
  );
}

/**
 * Says what will actually happen on this order rather than what the product is
 * capable of. An unattributed sale earns nobody anything, and a hint that reads
 * "earns $250" while the field is empty is a promise the order will not keep.
 */
function commissionHint(product: Product | undefined, marketerId: string): string {
  if (product === undefined || product.commissionCents === 0) {
    return "This product is not sold on commission.";
  }
  const amount = formatMoney(product.commissionCents, product.currency);
  return marketerId.length === 0
    ? `Leave empty for a direct sale. A marketer on this sale earns ${amount}.`
    : `Earns ${amount} when this order is marked paid.`;
}

/**
 * Creates the order only. Nothing is issued until it is marked paid, which is
 * the one place money turns into entitlement — recording the sale and granting
 * it are separate steps on purpose.
 */
function NewOrder({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [customerId, setCustomerId] = useState("");
  const [productId, setProductId] = useState("");
  const [planId, setPlanId] = useState("");
  const [marketerId, setMarketerId] = useState("");
  const [deployment, setDeployment] = useState<"self_hosted" | "managed">("self_hosted");
  const [hostnames, setHostnames] = useState("");
  const [price, setPrice] = useState("");
  const [notes, setNotes] = useState("");

  const customers = useQuery({
    queryKey: ["customers"],
    queryFn: () => api<Customer[]>("/api/v1/admin/customers"),
  });
  const products = useQuery({
    queryKey: ["products"],
    queryFn: () => api<Product[]>("/api/v1/admin/products"),
  });
  const plans = useQuery({
    queryKey: ["plans"],
    queryFn: () => api<Plan[]>("/api/v1/admin/plans"),
  });
  const marketers = useQuery({
    queryKey: ["marketers"],
    queryFn: () => api<Marketer[]>("/api/v1/admin/marketers"),
  });

  const product = (products.data ?? []).find((row) => row.id === productId);
  // A plan belongs to exactly one product, and the server rejects a mismatch.
  // Filtering here means that error is unreachable rather than merely handled.
  const planOptions = (plans.data ?? []).filter((plan) => plan.productId === productId);

  const create = useMutation({
    mutationFn: () =>
      api<Order>("/api/v1/admin/orders", {
        method: "POST",
        body: {
          customerId,
          productId,
          planId,
          marketerId: marketerId.length === 0 ? null : marketerId,
          deployment,
          hostnames: hostnames
            .split(/[\s,]+/)
            .map((value) => value.trim())
            .filter((value) => value.length > 0),
          ...(centsFromInput(price) === undefined ? {} : { oneTimeCents: centsFromInput(price) }),
          ...(notes.trim().length === 0 ? {} : { notes: notes.trim() }),
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["orders"] });
      onClose();
    },
  });

  return (
    <Card title="New order">
      <form
        className="space-y-4 p-4"
        onSubmit={(event) => {
          event.preventDefault();
          create.mutate();
        }}
      >
        <div className="grid gap-4 md:grid-cols-2">
          <Field label="Customer">
            <Select value={customerId} required onChange={(event) => setCustomerId(event.target.value)}>
              <option value="">Select a customer</option>
              {(customers.data ?? []).map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name} — {customer.email}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Product">
            <Select
              value={productId}
              required
              onChange={(event) => {
                setProductId(event.target.value);
                setPlanId("");
              }}
            >
              <option value="">Select a product</option>
              {(products.data ?? []).map((row) => (
                <option key={row.id} value={row.id}>
                  {row.name}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Maintenance plan" hint="What the customer is charged on renewal.">
            <Select
              value={planId}
              required
              disabled={productId.length === 0}
              onChange={(event) => setPlanId(event.target.value)}
            >
              <option value="">
                {productId.length === 0 ? "Pick a product first" : "Select a plan"}
              </option>
              {planOptions.map((plan) => (
                <option key={plan.id} value={plan.id}>
                  {plan.name} — {formatMoney(plan.priceCents, plan.currency)} / {plan.interval}
                </option>
              ))}
            </Select>
          </Field>

          <Field label="Marketer" hint={commissionHint(product, marketerId)}>
            <Select value={marketerId} onChange={(event) => setMarketerId(event.target.value)}>
              <option value="">Direct sale — no commission</option>
              {(marketers.data ?? [])
                .filter((marketer) => marketer.active)
                .map((marketer) => (
                  <option key={marketer.id} value={marketer.id}>
                    {marketer.name}
                  </option>
                ))}
            </Select>
          </Field>

          <Field label="Deployment" hint="Managed still gets a licence — we run it, it still checks in.">
            <Select
              value={deployment}
              onChange={(event) => setDeployment(event.target.value as "self_hosted" | "managed")}
            >
              <option value="self_hosted">Customer hosts it</option>
              <option value="managed">We host it</option>
            </Select>
          </Field>

          <Field
            label="One-time price"
            hint={
              product?.oneTimePriceCents == null
                ? "Leave empty to use the product's list price."
                : `List price is ${formatMoney(product.oneTimePriceCents, product.currency)}.`
            }
          >
            <Input
              type="number"
              min={0}
              step="0.01"
              value={price}
              placeholder="Negotiated price"
              onChange={(event) => setPrice(event.target.value)}
            />
          </Field>
        </div>

        <Field
          label="Allowed hostnames"
          hint="One per line. Where the licence will be permitted to run once it is issued."
        >
          <textarea
            value={hostnames}
            rows={3}
            placeholder="example.com&#10;www.example.com"
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 tabular text-ink placeholder:text-dim focus:border-accent"
            onChange={(event) => setHostnames(event.target.value)}
          />
        </Field>

        <Field label="Notes" hint="Anything about this deal worth remembering later.">
          <textarea
            value={notes}
            rows={2}
            className="w-full rounded-md border border-hairline-strong bg-base px-3 py-2 text-sm text-ink focus:border-accent"
            onChange={(event) => setNotes(event.target.value)}
          />
        </Field>

        {create.isError && <ErrorNote>{(create.error as Error).message}</ErrorNote>}

        <div className="flex gap-2">
          <Button type="submit" variant="primary" disabled={create.isPending}>
            {create.isPending ? "Creating…" : "Create order"}
          </Button>
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancel
          </Button>
        </div>
      </form>
    </Card>
  );
}
